import React, { useState } from "react";
import client, { whatsappLink } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { MessageCircle, Phone, Mail, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";

export function Contact() {
    const { settings } = useSettings();
    const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
    const upd = k => e => setForm({ ...form, [k]: e.target.value });
    const submit = async (e) => {
        e.preventDefault();
        try {
            await client.post("/enquiries", { type: "showroom", ...form });
            toast.success("Thank you. Our team will reach out shortly.");
            setForm({ name: "", phone: "", email: "", message: "" });
        } catch { toast.error("Could not submit"); }
    };
    return (
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-14">
            <div className="text-center mb-14">
                <div className="eyebrow">Visit Us</div>
                <h1 className="font-serif-luxe text-5xl mt-3">Contact Singh Jewellers</h1>
                <p className="mt-5 text-sm text-[color:var(--muted-brown)] max-w-md mx-auto">Come home to jewellery. Book a private viewing or drop by our Kotdwar showroom.</p>
            </div>
            <div className="grid lg:grid-cols-2 gap-10">
                <div className="card-luxe p-8">
                    <h2 className="font-serif-luxe text-2xl mb-6">The Showroom</h2>
                    <div className="space-y-4 text-sm">
                        <div className="flex items-start gap-3"><MapPin size={18} className="text-[color:var(--champagne)] flex-shrink-0" />{settings.address}</div>
                        <div className="flex items-center gap-3"><Phone size={18} className="text-[color:var(--champagne)]" />{settings.phone}</div>
                        <div className="flex items-center gap-3"><Mail size={18} className="text-[color:var(--champagne)]" />{settings.email}</div>
                        <div className="flex items-center gap-3"><Clock size={18} className="text-[color:var(--champagne)]" />{settings.hours}</div>
                    </div>
                    <div className="mt-6 flex gap-3">
                        <a href={whatsappLink(settings.whatsapp_number, "Hello Singh Jewellers, I'd like to book a showroom visit.")} target="_blank" rel="noreferrer" className="btn-whatsapp"><MessageCircle size={16} /> WhatsApp</a>
                        <a href={settings.maps_url || "#"} target="_blank" rel="noreferrer" className="btn-outline">Get Directions</a>
                    </div>
                    <div className="mt-8 aspect-video bg-[color:var(--beige)]">
                        <iframe title="map" className="w-full h-full" src={`https://maps.google.com/maps?q=${encodeURIComponent(settings.address || "Kotdwar")}&output=embed`} />
                    </div>
                </div>
                <form onSubmit={submit} className="card-luxe p-8 space-y-4">
                    <h2 className="font-serif-luxe text-2xl mb-4">Book a Visit</h2>
                    {["name", "phone", "email", "message"].map(k => (
                        <div key={k}>
                            <label className="text-xs tracking-[0.15em] uppercase text-[color:var(--muted-brown)]">{k}</label>
                            {k === "message" ? (
                                <textarea data-testid={`contact-${k}`} value={form[k]} onChange={upd(k)} rows={4} className="w-full border border-[color:var(--soft-border)] focus:border-[color:var(--champagne)] outline-none px-3 py-2 mt-1 bg-transparent" />
                            ) : (
                                <input data-testid={`contact-${k}`} type={k === "email" ? "email" : "text"} required={k !== "email"} value={form[k]} onChange={upd(k)} className="w-full border border-[color:var(--soft-border)] focus:border-[color:var(--champagne)] outline-none px-3 py-2.5 mt-1 bg-transparent" />
                            )}
                        </div>
                    ))}
                    <button className="btn-primary w-full" data-testid="contact-submit">Send Enquiry</button>
                </form>
            </div>
        </div>
    );
}

export function CustomJewellery() {
    const { settings } = useSettings();
    const [form, setForm] = useState({ name: "", phone: "", email: "", jewellery_type: "", budget: "", message: "", reference_image: "" });
    const upd = k => e => setForm({ ...form, [k]: e.target.value });
    const submit = async (e) => {
        e.preventDefault();
        try {
            await client.post("/enquiries", { type: "custom", ...form });
            toast.success("Thank you. Our design team will contact you soon.");
            setForm({ name: "", phone: "", email: "", jewellery_type: "", budget: "", message: "", reference_image: "" });
        } catch { toast.error("Could not submit"); }
    };
    return (
        <div>
            <section className="relative h-[50vh] overflow-hidden">
                <img src="https://images.unsplash.com/photo-1611085583191-a3b181a88401?w=1800" alt="Custom" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-[color:var(--charcoal)]/55 flex items-center">
                    <div className="max-w-[1440px] mx-auto px-6 lg:px-10 w-full">
                        <div className="eyebrow text-[color:var(--champagne-light)]">Bespoke</div>
                        <h1 className="font-serif-luxe text-5xl md:text-7xl text-white mt-3">Create Something Unique</h1>
                        <p className="text-white/80 mt-4 max-w-lg">Bring your idea. We will craft it into a keepsake — one that carries your story.</p>
                    </div>
                </div>
            </section>
            <section className="max-w-3xl mx-auto px-6 py-16">
                <form onSubmit={submit} className="card-luxe p-8 space-y-4">
                    <h2 className="font-serif-luxe text-2xl mb-3">Discuss Your Design</h2>
                    {["name", "phone", "email"].map(k => (
                        <div key={k}>
                            <label className="text-xs tracking-[0.15em] uppercase text-[color:var(--muted-brown)]">{k}</label>
                            <input data-testid={`custom-${k}`} type={k === "email" ? "email" : "text"} required={k !== "email"} value={form[k]} onChange={upd(k)} className="w-full border border-[color:var(--soft-border)] focus:border-[color:var(--champagne)] outline-none px-3 py-2.5 mt-1 bg-transparent" />
                        </div>
                    ))}
                    <div>
                        <label className="text-xs tracking-[0.15em] uppercase text-[color:var(--muted-brown)]">Jewellery Type</label>
                        <input data-testid="custom-type" value={form.jewellery_type} onChange={upd("jewellery_type")} placeholder="Ring, Necklace, Bangle…" className="w-full border border-[color:var(--soft-border)] focus:border-[color:var(--champagne)] outline-none px-3 py-2.5 mt-1 bg-transparent" />
                    </div>
                    <div>
                        <label className="text-xs tracking-[0.15em] uppercase text-[color:var(--muted-brown)]">Budget (optional)</label>
                        <input data-testid="custom-budget" value={form.budget} onChange={upd("budget")} placeholder="e.g. ₹50,000" className="w-full border border-[color:var(--soft-border)] focus:border-[color:var(--champagne)] outline-none px-3 py-2.5 mt-1 bg-transparent" />
                    </div>
                    <div>
                        <label className="text-xs tracking-[0.15em] uppercase text-[color:var(--muted-brown)]">Description</label>
                        <textarea data-testid="custom-message" value={form.message} onChange={upd("message")} rows={5} className="w-full border border-[color:var(--soft-border)] focus:border-[color:var(--champagne)] outline-none px-3 py-2 mt-1 bg-transparent" />
                    </div>
                    <div>
                        <label className="text-xs tracking-[0.15em] uppercase text-[color:var(--muted-brown)]">Reference Image URL (optional)</label>
                        <input data-testid="custom-ref" value={form.reference_image} onChange={upd("reference_image")} className="w-full border border-[color:var(--soft-border)] focus:border-[color:var(--champagne)] outline-none px-3 py-2.5 mt-1 bg-transparent" />
                    </div>
                    <button className="btn-primary w-full" data-testid="custom-submit">Submit Enquiry</button>
                    <a href={whatsappLink(settings.whatsapp_number, "Hello Singh Jewellers, I want to discuss a custom design.")} target="_blank" rel="noreferrer" className="btn-whatsapp w-full">
                        <MessageCircle size={16} /> Or Message on WhatsApp
                    </a>
                </form>
            </section>
        </div>
    );
}

export function About() {
    return (
        <div className="max-w-4xl mx-auto px-6 py-16">
            <div className="text-center mb-10">
                <div className="eyebrow">Our Story</div>
                <h1 className="font-serif-luxe text-5xl mt-3">About Singh Jewellers</h1>
            </div>
            <div className="space-y-6 text-[color:var(--charcoal)] font-serif-luxe italic text-lg leading-relaxed">
                <p>Singh Jewellers is a family-rooted jewellery showroom nestled in the heart of Kotdwar, Uttarakhand. We craft and curate gold, silver, diamond and bridal jewellery — pieces that carry the weight of tradition and the lightness of joy.</p>
                <p>Our promise is simple. Exceptional quality. Competitive, transparent making charges. And service that feels like family.</p>
            </div>
            <div className="grid md:grid-cols-3 gap-6 mt-12">
                {["Purity", "Quality", "Craftsmanship", "Transparent Pricing", "Personalised Service", "After-Sales Support"].map(t => (
                    <div key={t} className="card-luxe p-6 text-center"><div className="font-serif-luxe text-xl">{t}</div></div>
                ))}
            </div>
        </div>
    );
}

export function Blog() {
    const [posts, setPosts] = useState([]);
    React.useEffect(() => { client.get("/blog").then(r => setPosts(r.data)); }, []);
    return (
        <div className="max-w-[1440px] mx-auto px-6 lg:px-10 py-14">
            <div className="text-center mb-12">
                <div className="eyebrow">Jewellery Guide</div>
                <h1 className="font-serif-luxe text-5xl mt-3">Journal</h1>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
                {posts.map(p => (
                    <article key={p.id} className="card-luxe group">
                        <div className="relative aspect-[4/3] product-img-wrap">
                            <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                            <img className="watermark" src="/logo-watermark.png" alt="Singh Jewellers" />
                        </div>
                        <div className="p-6">
                            <h3 className="font-serif-luxe text-xl mb-2">{p.title}</h3>
                            <p className="text-sm text-[color:var(--muted-brown)]">{p.excerpt}</p>
                        </div>
                    </article>
                ))}
            </div>
        </div>
    );
}

export function NotFound() {
    return (
        <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6">
            <div className="font-serif-luxe text-8xl">404</div>
            <div className="eyebrow mt-3">Page Not Found</div>
            <p className="text-[color:var(--muted-brown)] mt-2 mb-6">The page you seek has wandered off. Let us guide you back.</p>
            <a href="/" className="btn-primary">Return Home</a>
        </div>
    );
}
